import { Bullet, Callout, LegalPage, Mail, P, Section, Strong } from '@/components/legal-page';
import { LEGAL_UPDATED, SITE_NAME } from '@/constants/site';

/**
 * The privacy policy App Store Connect and Google Play both require a URL for.
 *
 * Every claim below is a claim about code in this repo — the AsyncStorage key in
 * `game/review-prompt.ts`, the update check configured in `app.json`, the share
 * sheet in `app/result.tsx`. If any of those change, this page changes with them:
 * a privacy policy that overstates or understates what the app does is the kind
 * of mismatch store review reads against the privacy nutrition label.
 */
export default function PrivacyScreen() {
  return (
    <LegalPage
      kicker="LEGAL"
      title="Privacy Policy"
      subtitle={`Last updated ${LEGAL_UPDATED}`}
      description={`How ${SITE_NAME} handles data: no accounts, no analytics, no ads. Everything you play stays on your device.`}
      current="/privacy">
      <Callout>
        The short version: {SITE_NAME} has no accounts, asks for no personal information, and runs
        no analytics or advertising. Your drafts never leave your device.
      </Callout>

      <Section title="Who this covers">
        <P>
          This policy covers the {SITE_NAME} mobile app and the {SITE_NAME} website. It is written
          for the version of the app currently published; if the app starts collecting anything new,
          this page is updated before that version ships.
        </P>
      </Section>

      <Section title="What stays on your device">
        <P>
          The app stores exactly one thing locally: a flag recording that it has already asked you
          to rate it, so it never asks twice.
        </P>
        <Bullet>
          <Strong>Your draft, roster, and result</Strong> live in memory for the length of a session
          and are gone when the app closes. Nothing about a game is written to disk or sent
          anywhere.
        </Bullet>
        <Bullet>
          <Strong>The rating flag</Strong> is stored in the app&apos;s own sandboxed storage. It
          contains no personal information and is deleted when you delete the app.
        </Bullet>
      </Section>

      <Section title="What leaves your device">
        <P>Three things reach the network, and none of them carry anything about you:</P>
        <Bullet>
          <Strong>Update checks.</Strong> On launch the app asks Expo&apos;s update service whether a
          newer version of its code is available. That request carries the app version, runtime
          version, platform, and basic device and OS information — standard delivery metadata,
          handled by Expo under their own privacy policy at expo.dev/privacy.
        </Bullet>
        <Bullet>
          <Strong>The rating prompt.</Strong> The app asks your operating system to show
          Apple&apos;s or Google&apos;s own rating sheet. Whether you rate it, and what you rate,
          goes to the store — we never see it.
        </Bullet>
        <Bullet>
          <Strong>Sharing a result.</Strong> When you tap share, the app hands the text already on
          your screen to your system share sheet or clipboard. Where it goes from there is entirely
          your choice; nothing is sent on your behalf.
        </Bullet>
      </Section>

      <Section title="The website">
        <P>
          The web version is a static site. It sets no cookies and loads no analytics, advertising,
          or tracking scripts. Like any web host, the server that delivers it records standard
          request logs — IP address, browser user agent, and the page requested — used only to
          serve the site and protect it from abuse.
        </P>
      </Section>

      <Section title="What we never do">
        <Bullet>No advertising, ad networks, or ad identifiers.</Bullet>
        <Bullet>No third-party analytics, attribution, or crash-reporting SDKs.</Bullet>
        <Bullet>No tracking of you across other apps or websites.</Bullet>
        <Bullet>No access to your location, contacts, photos, microphone, or camera.</Bullet>
        <Bullet>No selling, renting, or sharing of personal data — there is none to sell.</Bullet>
      </Section>

      <Section title="Children">
        <P>
          {SITE_NAME} collects no personal information from anyone, including children under 13. We
          have no way to build a profile of a user, and no interest in one.
        </P>
      </Section>

      <Section title="Your choices">
        <P>
          Because nothing about you is stored off your device, there is no account to close and no
          data to request or export. Deleting the app removes everything it kept locally. If you
          have a question about any of this, ask and you will get a real answer.
        </P>
      </Section>

      <Section title="Changes to this policy">
        <P>
          If the app&apos;s data practices change, this page is updated and the date at the top is
          bumped. Material changes are called out in the app&apos;s release notes as well.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Questions about privacy: <Mail subject="Privacy question" />.
        </P>
      </Section>
    </LegalPage>
  );
}
